import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Landmark } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../app/hooks";
import { createMandateSetupSessionFromToken } from "../features/portal/slice-extensions/portal-slice-extension";
import { ROUTES } from "../routes";
import { isCardOnly } from "../features/portal/utils/setupMethods";

/**
 * Landing page for the emailed / hand-shared mandate-setup link. The secure token
 * in the URL is the authorization — one click hands the member off to Stripe's
 * hosted setup page (SEPA mandate or card, PD-0043; the link's `methods` picks the
 * copy). Also renders the success / cancelled return states.
 */
const MandateSetupPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const status = searchParams.get("status");
  const cardOnly = isCardOnly(searchParams.get("methods"));
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleContinue = async () => {
    if (!token) return;
    setError(null);
    setRedirecting(true);
    try {
      const url = await dispatch(createMandateSetupSessionFromToken(token)).unwrap();
      window.location.assign(url);
    } catch {
      setError(t("portal.mandateSetup.invalid", "This link is invalid or has expired."));
      setRedirecting(false);
    }
  };

  return (
    <section className="mx-auto w-full max-w-md px-4 py-12 text-center">
      <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-95 dark:bg-primary-20">
        <Landmark size={26} className="text-primary-40 dark:text-primary-90" />
      </div>

      {status === "success" && (
        <>
          <h1 className="mb-2 text-2xl font-bold text-grey-20 dark:text-white">
            {t("portal.mandateSetup.successTitle", "Automatic payments are set up")}
          </h1>
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {cardOnly
              ? t("portal.mandateSetup.successBodyCard", "Thank you — future payments will be charged to your card automatically.")
              : t("portal.mandateSetup.successBody",
                "Thank you — future payments will be collected automatically. For a direct debit you're notified by email before each one.")}
          </p>
          <Link to={ROUTES.PORTAL_LOGIN} className="btn btn-secondary mt-6 inline-flex">
            {t("portal.mandateSetup.openPortal", "Open the member portal")}
          </Link>
        </>
      )}

      {status === "cancelled" && (
        <>
          <h1 className="mb-2 text-2xl font-bold text-grey-20 dark:text-white">
            {t("portal.mandateSetup.cancelledTitle", "Setup not completed")}
          </h1>
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {t("portal.mandateSetup.cancelledBody",
              "Nothing was saved. You can reopen the link from your email to try again.")}
          </p>
        </>
      )}

      {!status && token && (
        <>
          <h1 className="mb-2 text-2xl font-bold text-grey-20 dark:text-white">
            {t("portal.mandateSetup.title", "Set up automatic payments")}
          </h1>
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {cardOnly
              ? t("portal.mandateSetup.introCard",
                "You'll enter your card once on our payment partner Stripe's secure page. Future payments are then charged automatically.")
              : t("portal.mandateSetup.intro",
                "You'll enter your bank details or a card once on our payment partner Stripe's secure page. Future payments are then collected automatically — for a direct debit you're notified by email before each one.")}
          </p>
          {error && (
            <p className="mt-3 text-sm font-medium text-red-700 dark:text-red-400">{error}</p>
          )}
          <button
            type="button"
            className="btn btn-primary mt-6"
            disabled={redirecting}
            onClick={handleContinue}
          >
            {t("portal.mandateSetup.continue", "Continue to Stripe")}
          </button>
        </>
      )}

      {!status && !token && (
        <>
          <h1 className="mb-2 text-2xl font-bold text-grey-20 dark:text-white">
            {t("portal.mandateSetup.invalidTitle", "Link not valid")}
          </h1>
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {t("portal.mandateSetup.invalid", "This link is invalid or has expired.")}
          </p>
        </>
      )}
    </section>
  );
};

export default MandateSetupPage;
